import React, { useState, useEffect, useCallback, useRef } from 'react';
import { message, Select } from 'antd';
import { getWordsByUnit } from '../../services/wordService';
import cardsImage from '../../static/image/cards.png';
import playIcon from '../../static/image/播放.png';
import listenIcon from '../../static/image/听.png';
import refreshIcon from '../../static/image/刷新.png';
import './Exercises.css';

// 题型常量
const QUESTION_TYPES = {
  WORD_PICTURE_MATCH: 'word_picture_match',
  LISTEN_SELECT: 'listen_select',
  WORD_SPELLING: 'word_spelling',
};

const Exercises = () => {
  // 单元和课时状态
  const [unitId, setUnitId] = useState(null);
  const [wordsData, setWordsData] = useState(null); // 存储完整的词汇数据（包含课时和单词）
  const [selectedLessons, setSelectedLessons] = useState([]);
  
  // 题型选择状态
  const [questionType, setQuestionType] = useState(QUESTION_TYPES.WORD_PICTURE_MATCH);
  
  // 词汇数据状态
  const [allWords, setAllWords] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  
  // 分别存储三种题型的题目
  const [wordPictureQuestions, setWordPictureQuestions] = useState([]);
  const [listenSelectQuestions, setListenSelectQuestions] = useState([]);
  const [wordSpellingQuestions, setWordSpellingQuestions] = useState([]);
  
  // 词图匹配状态
  const [selectedPicture, setSelectedPicture] = useState(null);
  const [selectedWord, setSelectedWord] = useState(null);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [errorItems, setErrorItems] = useState({ picture: null, word: null });
  const [combiningItems, setCombiningItems] = useState([]); // 正在移动到中间的项目ID
  const [combiningPair, setCombiningPair] = useState(null); // 正在组合的配对（图片在上，词汇在下）
  const [showCardsDetail, setShowCardsDetail] = useState(false); // 是否显示卡片详情弹窗
  const [isPlayingAudio, setIsPlayingAudio] = useState(false); // 是否正在播放音频
  const [playingCardIndex, setPlayingCardIndex] = useState(null); // 正在播放的卡片索引
  const [showCardBag, setShowCardBag] = useState(false); // 是否显示卡片袋
  
  // 保存每道词图匹配题的答题状态
  const [wordMatchAnswerStates, setWordMatchAnswerStates] = useState({});
  
  // 听音选词状态
  const [listenOptions, setListenOptions] = useState([]); // 当前题目的4个选项
  const [selectedOption, setSelectedOption] = useState(null); // 选中的选项
  const [isCorrect, setIsCorrect] = useState(null); // 是否答对（null: 未答, true: 正确, false: 错误）
  const [errorOption, setErrorOption] = useState(null); // 错误选项（用于抖动效果）
  
  // 单词拼写状态
  const [selectedLetters, setSelectedLetters] = useState([]); // 已选择的字母数组
  const [availableLetters, setAvailableLetters] = useState([]); // 可用的字母数组（右侧）
  const [spellingStatus, setSpellingStatus] = useState([]); // 每个字母的状态（correct/incorrect）
  const [isSpellingComplete, setIsSpellingComplete] = useState(false); // 是否完成拼写
  
  // 音频引用（用于停止播放）
  const currentAudioRef = useRef(null);
  
  // 掉落动画定时器引用（用于清理）
  const fallingTimersRef = useRef([]);
  
  // 正在匹配的卡片信息（用于切换题目时立即完成）
  const currentMatchingRef = useRef(null);
  
  // 听音选词自动跳转定时器引用（用于清理）
  const listenAutoNextTimerRef = useRef(null);
  
  // 加载状态
  const [loading, setLoading] = useState(false);
  const [exerciseStarted, setExerciseStarted] = useState(false);

  // 设置页面背景样式
  useEffect(() => {
    document.body.classList.add('exercises-page');
    
    return () => {
      document.body.classList.remove('exercises-page');
      // 清理音频资源
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
      }
      // 清理所有掉落动画定时器
      fallingTimersRef.current.forEach(timerId => clearTimeout(timerId));
      fallingTimersRef.current = [];
      // 清理听音选词自动跳转定时器
      if (listenAutoNextTimerRef.current) {
        clearTimeout(listenAutoNextTimerRef.current);
        listenAutoNextTimerRef.current = null;
      }
    };
  }, []);

  // 监听单元变化
  useEffect(() => {
    const handleUnitChange = (event) => {
      const { unitId: newUnitId } = event.detail;
      setUnitId(newUnitId);
      // 请求所有单词，不添加任何限制条件
      loadWordsData(newUnitId);
      resetExercise();
    };

    window.addEventListener('unitChanged', handleUnitChange);

    // 初始加载
    const savedData = localStorage.getItem('selectedTextbook');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        if (parsed.unit && parsed.unit.id) {
          setUnitId(parsed.unit.id);
          // 初始加载时，请求所有单词
          loadWordsData(parsed.unit.id);
        }
      } catch (error) {
        console.error('解析localStorage数据失败:', error);
      }
    }

    return () => {
      window.removeEventListener('unitChanged', handleUnitChange);
    };
  }, []);

  // 加载词汇数据（包含课时和单词）
  const loadWordsData = async (unitId) => {
    if (!unitId) return;
    
    setLoading(true);
    try {
      // 使用 getWordsByUnit 一次性获取单元的所有课时和单词
      // 只保留 is_active 条件，不添加其他过滤条件
      const filters = { is_active: true, source_type: '2' };
      const data = await getWordsByUnit(unitId, filters);
      setWordsData(data);
      
      // 默认选择第一个课时
      if (data && data.lessons && data.lessons.length > 0) {
        setSelectedLessons([data.lessons[0].lesson_id]);
      }
    } catch (error) {
      console.error('加载词汇数据失败:', error);
      message.error('加载词汇数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 监听题型变化不需要重置状态，因为导航逻辑会处理
  // 题型切换时的题目和索引变化

  // 自动开始练习：监听课时、单元变化（一次性生成三种题型的所有题目）
  useEffect(() => {
    // 停止正在进行的匹配（停止音频和动画）
    finishCurrentMatching();
    
    // 先重置三种题型的状态，避免切换时的状态残留
    resetCurrentQuestion();
    resetListenQuestion();
    resetSpellingQuestion();
    
    // 清空卡片袋（课时改变时）
    setMatchedPairs([]);
    setWordMatchAnswerStates({});
    setShowCardBag(false);
    
    // 确保有必要的数据才自动开始（并且数据不在加载中）
    if (selectedLessons.length === 0 || !wordsData || loading) {
      return;
    }

    const words = getSelectedWords();
    if (!words || words.length === 0) {
      return;
    }

    // 一次性生成三种题型的所有题目
    const matchQuestions = generateWordPictureQuestions(words);
    const selectQuestions = generateListenSelectQuestions(words);
    const spellingQuestions = generateWordSpellingQuestions(words);
    
    // 保存三种题型的题目
    setWordPictureQuestions(matchQuestions);
    setListenSelectQuestions(selectQuestions);
    setWordSpellingQuestions(spellingQuestions);
    
    // 根据当前题型设置显示的题目
    if (questionType === QUESTION_TYPES.WORD_PICTURE_MATCH) {
      if (matchQuestions && matchQuestions.length > 0) {
        setQuestions(matchQuestions);
        setCurrentQuestionIndex(0);
        setExerciseStarted(true);
      }
    } else if (questionType === QUESTION_TYPES.LISTEN_SELECT) {
      if (selectQuestions && selectQuestions.length > 0) {
        setQuestions(selectQuestions);
        setCurrentQuestionIndex(0);
        setExerciseStarted(true);
        // 直接播放第一题音频（使用生成的题目数组）
        playListenAudioDirectly(selectQuestions[0]);
      }
    } else if (questionType === QUESTION_TYPES.WORD_SPELLING) {
      if (spellingQuestions && spellingQuestions.length > 0) {
        setQuestions(spellingQuestions);
        setCurrentQuestionIndex(0);
        setExerciseStarted(true);
        // 初始化第一题的字母
        initSpellingQuestion(spellingQuestions[0]);
      }
    }
  }, [selectedLessons, unitId, wordsData, loading]);
  
  // 监听题型切换，更新当前显示的题目
  useEffect(() => {
    if (questionType === QUESTION_TYPES.WORD_PICTURE_MATCH && wordPictureQuestions.length > 0) {
      setQuestions(wordPictureQuestions);
    } else if (questionType === QUESTION_TYPES.LISTEN_SELECT && listenSelectQuestions.length > 0) {
      setQuestions(listenSelectQuestions);
      // 播放当前题目的音频
      if (listenSelectQuestions[currentQuestionIndex]) {
        setTimeout(() => {
          playListenAudioDirectly(listenSelectQuestions[currentQuestionIndex]);
        }, 100);
      }
    } else if (questionType === QUESTION_TYPES.WORD_SPELLING && wordSpellingQuestions.length > 0) {
      setQuestions(wordSpellingQuestions);
      // 初始化拼写题目
      if (wordSpellingQuestions[currentQuestionIndex]) {
        setTimeout(() => {
          initSpellingQuestion(wordSpellingQuestions[currentQuestionIndex]);
        }, 100);
      }
    }
  }, [questionType]);

  // 根据选中的课时获取单词
  const getSelectedWords = () => {
    if (!wordsData || !wordsData.lessons || selectedLessons.length === 0) {
      return [];
    }

    const allWordsData = [];
    selectedLessons.forEach(lessonId => {
      const lesson = wordsData.lessons.find(l => l.lesson_id === lessonId);
      if (lesson && lesson.words) {
        // 转换数据格式，适配练习页面需要的字段
        const convertedWords = lesson.words.map(word => ({
          id: word.id,
          word: word.title, // API使用title字段
          chinese: word.en_title || word.title, // 如果有en_title就用，否则用title
          image_url: word.images && word.images.length > 0 ? word.images[0].res : '', // 使用第一张图片的res字段
          audio: word.audio?.res || word.sounds?.[0]?.res || '', // 音频URL - 使用word.audio.res
          is_bold: word.word_type_id === 1,
          is_match: word.is_match || false, // 保留词图匹配标记
          is_select_picture: word.is_select_picture || false, // 保留听音选词标记
          is_word_spelling: word.is_word_spelling || false // 保留单词拼写标记
        }));
        allWordsData.push(...convertedWords);
      }
    });

    return allWordsData;
  };

  // compatible_id 互斥工具函数
  const toIdPair = (w) => {
    const idStr = w?.id != null ? String(w.id) : '';
    const compatRaw = w?.compatible_id ?? w?.compatibleId;
    let compatIds = [];
    if (compatRaw != null && compatRaw !== '') {
      const compatStr = String(compatRaw).trim();
      if (compatStr) {
        compatIds = compatStr.split(';').map(id => id.trim()).filter(id => id);
      }
    }
    return { idStr, compatIds };
  };

  // 判断两个词是否属于同一兼容组
  const isSameCompatGroup = (a, b) => {
    if (!a || !b) return false;
    const { idStr: aId, compatIds: aCompatIds } = toIdPair(a);
    const { idStr: bId, compatIds: bCompatIds } = toIdPair(b);
    if (aId && bId && aId === bId) return true;
    if (aId && bCompatIds.includes(aId)) return true;
    if (bId && aCompatIds.includes(bId)) return true;
    if (aCompatIds.length > 0 && bCompatIds.length > 0) {
      for (const aCompatId of aCompatIds) {
        if (bCompatIds.includes(aCompatId)) return true;
      }
    }
    return false;
  };

  // 检查一个词是否与已选词组中的任何一个互斥
  const hasCompatConflict = (word, group) => {
    return group.some(w => isSameCompatGroup(word, w));
  };

  // 生成词图匹配题目
  const generateWordPictureQuestions = (words) => {
    if (!words || words.length === 0) return [];

    // 只选择 is_match 为 true 的单词
    const matchWords = words.filter(word => word.is_match === true);
    if (matchWords.length === 0) return [];

    const questions = [];
    // 先对所有单词进行随机打乱
    let remainingWords = [...matchWords].sort(() => Math.random() - 0.5);

    while (remainingWords.length > 0) {
      let questionWords = [];
      let skippedWords = [];

      // 从 remainingWords 中逐个取词，跳过与已选词互斥的
      for (const word of remainingWords) {
        if (questionWords.length >= 5) {
          skippedWords.push(word);
          continue;
        }
        if (hasCompatConflict(word, questionWords)) {
          skippedWords.push(word);
        } else {
          questionWords.push(word);
        }
      }

      // 如果不足5个，从全部匹配词中随机补充（排除已选词和互斥词）
      if (questionWords.length < 5) {
        const needed = 5 - questionWords.length;
        const questionWordIds = new Set(questionWords.map(w => String(w.id)));
        const availableWords = matchWords
          .filter(w => !questionWordIds.has(String(w.id)) && !hasCompatConflict(w, questionWords))
          .sort(() => Math.random() - 0.5);
        
        // 逐个添加，每次添加后重新检查互斥
        for (const candidate of availableWords) {
          if (questionWords.length >= 5) break;
          if (!hasCompatConflict(candidate, questionWords)) {
            questionWords.push(candidate);
          }
        }

        // 如果仍然不足5个（极端情况：互斥关系太多），放宽限制直接补充
        if (questionWords.length < 5) {
          const stillNeeded = 5 - questionWords.length;
          const usedIds = new Set(questionWords.map(w => String(w.id)));
          const fallback = matchWords
            .filter(w => !usedIds.has(String(w.id)))
            .sort(() => Math.random() - 0.5)
            .slice(0, stillNeeded);
          questionWords = [...questionWords, ...fallback];
        }
      }

      remainingWords = skippedWords;

      // 打乱顺序
      const shuffledWords = questionWords.sort(() => Math.random() - 0.5);
      const shuffledPictures = [...questionWords].sort(() => Math.random() - 0.5);

      questions.push({
        id: questions.length + 1,
        words: shuffledWords,
        pictures: shuffledPictures,
      });
    }

    return questions;
  };

  // 生成听音选词题目
  const generateListenSelectQuestions = (words) => {
    if (!words || words.length === 0) return [];

    // 只选择 is_select_picture 为 true 的单词
    const selectWords = words.filter(word => word.is_select_picture === true);
    if (selectWords.length === 0) return [];

    const questions = [];
    
    // 先打乱单词顺序
    const shuffledWords = [...selectWords].sort(() => Math.random() - 0.5);
    
    // 每个单词都是一道题
    shuffledWords.forEach((word, index) => {
      // 生成3个干扰项
      const distractors = [];
      const availableWords = selectWords.filter(w => w.id !== word.id);
      
      // 随机选择3个不同的干扰项
      const shuffled = [...availableWords].sort(() => Math.random() - 0.5);
      for (let i = 0; i < Math.min(3, shuffled.length); i++) {
        distractors.push(shuffled[i]);
      }
      
      // 如果干扰项不足3个，重复使用
      while (distractors.length < 3 && availableWords.length > 0) {
        const randomWord = availableWords[Math.floor(Math.random() * availableWords.length)];
        if (!distractors.find(d => d.id === randomWord.id)) {
          distractors.push(randomWord);
        }
      }
      
      // 将正确答案和干扰项混合并打乱
      const options = [word, ...distractors].sort(() => Math.random() - 0.5);
      
      questions.push({
        id: index + 1,
        correctWord: word,
        options: options,
      });
    });

    return questions;
  };

  // 生成单词拼写题目
  // 智能分割字符串，将省略号 "..." 作为一个整体
  const smartSplitWord = (word) => {
    const result = [];
    let i = 0;
    
    while (i < word.length) {
      // 检查是否是省略号（三个连续的点）
      if (i <= word.length - 3 && word.substring(i, i + 3) === '...') {
        result.push('...');
        i += 3;
      } else {
        result.push(word[i]);
        i += 1;
      }
    }
    
    return result;
  };

  // 判断字符类型
  const getCharacterType = (char) => {
    if (char === ' ') return 'space';
    if (char === '...') return 'ellipsis';
    // 标点符号：. , ! ? ; : ' " - ( ) [ ] { } / \ @ # $ % & * + = ~ ` | < > _
    if (/[.,!?;:'"()\[\]{}\\/\-@#$%&*+=~`|<>_]/.test(char)) return 'punctuation';
    return 'normal';
  };

  // 获取字符的显示文本
  const getCharacterDisplay = (char) => {
    if (char === ' ') return '␣'; // 空格显示为可见符号
    return char; // 其他字符直接显示
  };

  const generateWordSpellingQuestions = (words) => {
    if (!words || words.length === 0) return [];

    // 只选择 is_word_spelling 为 true 的单词
    const spellingWords = words.filter(word => word.is_word_spelling === true);
    if (spellingWords.length === 0) return [];

    const questions = [];
    
    // 先打乱单词顺序
    const shuffledWords = [...spellingWords].sort(() => Math.random() - 0.5);
    
    // 每个单词都是一道题
    shuffledWords.forEach((word, index) => {
      // 使用智能分割函数，将省略号作为一个整体
      const letters = smartSplitWord(word.word);
      
      // 先给每个字母添加唯一标识和原始索引
      const lettersWithIndex = letters.map((letter, idx) => ({
        id: `${word.id}-${idx}`,
        letter: letter,
        originalIndex: idx
      }));
      
      // 打乱字母顺序，确保打乱后的顺序与原始顺序不同
      let shuffledLetters = [...lettersWithIndex];
      let attempts = 0;
      const maxAttempts = 50; // 最多尝试50次
      
      // 如果单词长度大于1，则确保打乱后的顺序不等于原始顺序
      if (letters.length > 1) {
        do {
          shuffledLetters = [...lettersWithIndex].sort(() => Math.random() - 0.5);
          attempts++;
          
          // 检查是否与原始顺序相同
          const isSameOrder = shuffledLetters.every((item, idx) => item.originalIndex === idx);
          
          // 如果顺序不同，或者尝试次数过多，则跳出循环
          if (!isSameOrder || attempts >= maxAttempts) {
            break;
          }
        } while (true);
      }
      
      questions.push({
        id: index + 1,
        word: word,
        correctWord: word.word,
        chinese: word.chinese,
        shuffledLetters: shuffledLetters
      });
    });

    return questions;
  };


  // 保存当前词图匹配题目的答题状态
  const saveCurrentMatchState = (questionIndex) => {
    setWordMatchAnswerStates(prev => ({
      ...prev,
      [questionIndex]: {
        matchedPairs: [...matchedPairs]
      }
    }));
  };

  // 恢复词图匹配题目的答题状态
  const restoreMatchState = (questionIndex) => {
    const savedState = wordMatchAnswerStates[questionIndex];
    if (savedState && savedState.matchedPairs.length > 0) {
      setMatchedPairs(savedState.matchedPairs);
      // 如果有已匹配的配对，显示卡片袋
      setShowCardBag(true);
    } else {
      setMatchedPairs([]);
      setShowCardBag(false);
    }
    // 清空其他临时状态
    setSelectedPicture(null);
    setSelectedWord(null);
    setErrorItems({ picture: null, word: null });
    setCombiningItems([]);
    setCombiningPair(null);
    setShowCardsDetail(false);
    setIsPlayingAudio(false);
  };

  // 重置当前题目状态（词图匹配）- 保留原有功能用于初始化
  // 清理所有掉落动画定时器
  const clearFallingTimers = () => {
    fallingTimersRef.current.forEach(timerId => clearTimeout(timerId));
    fallingTimersRef.current = [];
  };

  // 立即完成当前正在匹配的卡片（切换题目前调用）
  const finishCurrentMatching = () => {
    // 停止音频播放
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    
    // 清除所有定时器
    clearFallingTimers();
    
    // 如果有正在匹配的卡片，立即将其加入卡片袋
    if (currentMatchingRef.current) {
      const { matchedPair, questionIndex } = currentMatchingRef.current;
      
      // 立即添加到已匹配列表
      setMatchedPairs(prev => {
        const newMatchedPairs = [...prev, matchedPair];
        // 保存状态到对应题目的 wordMatchAnswerStates
        setWordMatchAnswerStates(states => ({
          ...states,
          [questionIndex]: {
            matchedPairs: newMatchedPairs
          }
        }));
        return newMatchedPairs;
      });
      
      // 清除组合状态
      setCombiningPair(null);
      setCombiningItems([]);
      
      // 清除匹配信息
      currentMatchingRef.current = null;
    }
  };

  const resetCurrentQuestion = () => {
    // 清理所有待执行的掉落动画定时器
    clearFallingTimers();
    
    setSelectedPicture(null);
    setSelectedWord(null);
    setMatchedPairs([]);
    setErrorItems({ picture: null, word: null });
    setCombiningItems([]);
    setCombiningPair(null);
    setShowCardsDetail(false);
    setIsPlayingAudio(false);
    setShowCardBag(false);
  };

  // 重置听音选词题目状态
  const resetListenQuestion = () => {
    // 先停止当前播放的音频
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    // 清除自动跳转定时器
    if (listenAutoNextTimerRef.current) {
      clearTimeout(listenAutoNextTimerRef.current);
      listenAutoNextTimerRef.current = null;
    }
    setIsPlayingAudio(false);
    setSelectedOption(null);
    setIsCorrect(null);
    setErrorOption(null);
  };

  // 重置单词拼写题目状态
  const resetSpellingQuestion = () => {
    setSelectedLetters([]);
    setAvailableLetters([]);
    setSpellingStatus([]);
    setIsSpellingComplete(false);
  };

  // 重置整个练习
  const resetExercise = () => {
    setExerciseStarted(false);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    resetCurrentQuestion();
    resetListenQuestion();
    resetSpellingQuestion();
  };

  // 处理图片点击
  const handlePictureClick = (picture) => {
    if (matchedPairs.find(p => p.pictureId === picture.id)) return;
    
    setSelectedPicture(picture);
    setErrorItems({ picture: null, word: null });

    // 如果已经选择了单词，进行匹配检查
    if (selectedWord) {
      checkMatch(picture, selectedWord);
    }
  };

  // 处理单词点击
  const handleWordClick = (word) => {
    if (matchedPairs.find(p => p.wordId === word.id)) return;
    
    setSelectedWord(word);
    setErrorItems({ picture: null, word: null });

    // 如果已经选择了图片，进行匹配检查
    if (selectedPicture) {
      checkMatch(selectedPicture, word);
    }
  };

  // 检查匹配
  const checkMatch = (picture, word) => {
    if (picture.id === word.id) {
      // 如果有正在进行的匹配，立即完成它（停止音频和动画，直接进入卡片袋）
      finishCurrentMatching();
      
      // 保存当前题目索引，确保卡片加入到正确的题目
      const questionIndexForThisMatch = currentQuestionIndex;
      
      // 匹配正确 - 创建组合配对
      const matchedPair = {
        pictureId: picture.id,
        wordId: word.id,
        word: word.word,
        chinese: word.chinese,
        imageUrl: picture.image_url,
        audioUrl: word.audio,
        questionIndex: questionIndexForThisMatch, // 保存题目索引
      };
      
      // 直接隐藏已匹配的图片和单词
      setCombiningItems([picture.id, word.id]);
      
      // 直接组合成卡片（不需要等待飞行动画）
      setCombiningPair(matchedPair);
      
      // 播放音频并等待音频播放完成后再掉落
      if (word.audio) {
        const audio = new Audio(word.audio);
        
        // 保存音频引用和匹配信息
        currentAudioRef.current = audio;
        currentMatchingRef.current = { matchedPair, questionIndex: questionIndexForThisMatch };
        
        // 监听音频播放完成事件
        audio.addEventListener('ended', () => {
          // 音频播放完成后，显示卡片袋
          setShowCardBag(true);
          
          // 执行掉落动画
          const timer1 = setTimeout(() => {
            setCombiningPair({ ...matchedPair, isFalling: true });
            
            // 掉落完成后，添加到已匹配列表
            const timer2 = setTimeout(() => {
              setCombiningPair(null);
              setCombiningItems([]);
              setMatchedPairs(prev => {
                const newMatchedPairs = [...prev, matchedPair];
                // 保存状态到 wordMatchAnswerStates（使用保存的题目索引）
                setWordMatchAnswerStates(states => ({
                  ...states,
                  [questionIndexForThisMatch]: {
                    matchedPairs: newMatchedPairs
                  }
                }));
                return newMatchedPairs;
              });
              // 清除当前匹配信息
              currentMatchingRef.current = null;
              currentAudioRef.current = null;
            }, 300); // 掉落动画时间
            fallingTimersRef.current.push(timer2);
          }, 50); // 音频播放结束后的短暂延迟
          fallingTimersRef.current.push(timer1);
        });
        
        // 播放音频
        audio.play().catch(error => {
          console.error('播放音频失败:', error);
          // 即使播放失败也显示卡片袋和执行掉落动画
          setShowCardBag(true);
          const timer1 = setTimeout(() => {
            setCombiningPair({ ...matchedPair, isFalling: true });
            const timer2 = setTimeout(() => {
              setCombiningPair(null);
              setCombiningItems([]);
              setMatchedPairs(prev => {
                const newMatchedPairs = [...prev, matchedPair];
                // 保存状态到 wordMatchAnswerStates（使用保存的题目索引）
                setWordMatchAnswerStates(states => ({
                  ...states,
                  [questionIndexForThisMatch]: {
                    matchedPairs: newMatchedPairs
                  }
                }));
                return newMatchedPairs;
              });
              // 清除当前匹配信息
              currentMatchingRef.current = null;
              currentAudioRef.current = null;
            }, 300);
            fallingTimersRef.current.push(timer2);
          }, 300);
          fallingTimersRef.current.push(timer1);
        });
      } else {
        // 没有音频，直接显示卡片袋并等待后执行掉落
        currentMatchingRef.current = { matchedPair, questionIndex: questionIndexForThisMatch };
        setShowCardBag(true);
        const timer1 = setTimeout(() => {
          setCombiningPair({ ...matchedPair, isFalling: true });
          const timer2 = setTimeout(() => {
            setCombiningPair(null);
            setCombiningItems([]);
            setMatchedPairs(prev => {
              const newMatchedPairs = [...prev, matchedPair];
              // 保存状态到 wordMatchAnswerStates（使用保存的题目索引）
              setWordMatchAnswerStates(states => ({
                ...states,
                [questionIndexForThisMatch]: {
                  matchedPairs: newMatchedPairs
                }
              }));
              return newMatchedPairs;
            });
            // 清除当前匹配信息
            currentMatchingRef.current = null;
          }, 300);
          fallingTimersRef.current.push(timer2);
        }, 300);
        fallingTimersRef.current.push(timer1);
      }
      
      setSelectedPicture(null);
      setSelectedWord(null);
    } else {
      // 匹配错误 - 显示红色波纹和震动，并播放错误音效
      setErrorItems({ picture: picture.id, word: word.id });
      
      // 播放错误音效
      const audio = new Audio('/audio/错误.MP3');
      audio.play().catch(error => {
        console.error('播放错误音效失败:', error);
      });
      
      const errorTimer = setTimeout(() => {
        setErrorItems({ picture: null, word: null });
        setSelectedPicture(null);
        setSelectedWord(null);
      }, 600);
      fallingTimersRef.current.push(errorTimer);
    }
  };

  // 播放音频
  const handlePlayAudio = (pair, index) => {
    if (pair.audioUrl) {
      setPlayingCardIndex(index);
      const audio = new Audio(pair.audioUrl);
      
      audio.addEventListener('ended', () => {
        setPlayingCardIndex(null);
      });
      
      audio.addEventListener('error', () => {
        setPlayingCardIndex(null);
      });
      
      audio.play().catch(error => {
        console.error('播放音频失败:', error);
        message.error('播放音频失败');
        setPlayingCardIndex(null);
      });
    } else {
      message.info('该单词暂无音频');
    }
  };

  // 停止当前正在播放的音频
  const stopCurrentAudio = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
      setIsPlayingAudio(false);
    }
  };

  // 听音选词：播放当前题目音频
  const playListenAudio = () => {
    // 先停止当前播放的音频
    stopCurrentAudio();
    
    const currentQuestion = questions[currentQuestionIndex];
    if (!currentQuestion || !currentQuestion.correctWord.audio) {
      message.warning('该单词没有音频');
      return;
    }
    
    setIsPlayingAudio(true);
    const audio = new Audio(currentQuestion.correctWord.audio);
    currentAudioRef.current = audio;
    
    audio.addEventListener('ended', () => {
      setIsPlayingAudio(false);
      currentAudioRef.current = null;
    });
    
    audio.play().catch(error => {
      console.error('音频播放失败:', error);
      message.error('音频播放失败');
      setIsPlayingAudio(false);
      currentAudioRef.current = null;
    });
  };

  // 听音选词：直接播放音频（传入题目对象）
  const playListenAudioDirectly = (question) => {
    // 先停止当前播放的音频
    stopCurrentAudio();
    
    if (!question || !question.correctWord || !question.correctWord.audio) {
      return;
    }
    
    setIsPlayingAudio(true);
    const audio = new Audio(question.correctWord.audio);
    currentAudioRef.current = audio;
    
    audio.addEventListener('ended', () => {
      setIsPlayingAudio(false);
      currentAudioRef.current = null;
    });
    
    audio.play().catch(error => {
      console.error('音频播放失败:', error);
      setIsPlayingAudio(false);
      currentAudioRef.current = null;
    });
  };

  // 听音选词：切换题目后播放音频（传入题目索引）
  const playListenAudioAfterQuestionChange = (questionIndex) => {
    const question = questions[questionIndex];
    playListenAudioDirectly(question);
  };

  // 听音选词：处理选项点击
  const handleListenOptionClick = (option) => {
    // 已经答过题了，不允许再选
    if (isCorrect !== null) return;
    
    const currentQuestion = questions[currentQuestionIndex];
    const correct = option.id === currentQuestion.correctWord.id;
    
    setSelectedOption(option);
    setIsCorrect(correct);
    
    if (correct) {
      // 答对了，播放正确音效
      const audio = new Audio('/audio/正确.MP3');
      audio.play().catch(error => {
        console.error('播放正确音效失败:', error);
      });
      
      // 1.5秒后自动进入下一题（保存定时器引用以便清理）
      listenAutoNextTimerRef.current = setTimeout(() => {
        if (currentQuestionIndex < questions.length - 1) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
          resetListenQuestion();
          // 延迟播放下一题音频
          setTimeout(() => {
            playListenAudioAfterQuestionChange(currentQuestionIndex + 1);
          }, 100);
        } else if (wordSpellingQuestions.length > 0) {
          // 如果是听音选词的最后一题，切换到单词拼写的第一题
          setQuestionType(QUESTION_TYPES.WORD_SPELLING);
          setCurrentQuestionIndex(0);
          resetSpellingQuestion();
          // 初始化单词拼写第一题
          setTimeout(() => {
            if (wordSpellingQuestions[0]) {
              initSpellingQuestion(wordSpellingQuestions[0]);
            }
          }, 100);
        } else {
          message.success('恭喜完成所有题目！');
        }
        listenAutoNextTimerRef.current = null; // 定时器执行完毕，清空引用
      }, 1500);
    } else {
      // 答错了，播放错误音效并显示抖动效果
      const audio = new Audio('/audio/错误.MP3');
      audio.play().catch(error => {
        console.error('播放错误音效失败:', error);
      });
      
      setErrorOption(option.id);
      
      // 600ms后清除错误状态，允许继续作答
      setTimeout(() => {
        setErrorOption(null);
        setSelectedOption(null);
        setIsCorrect(null);
      }, 600);
    }
  };

  // 单词拼写：初始化题目
  const initSpellingQuestion = (question) => {
    if (!question) return;
    
    // 重置状态
    setSelectedLetters([]);
    setSpellingStatus([]);
    setIsSpellingComplete(false);
    
    // 设置可用字母
    setAvailableLetters(question.shuffledLetters);
  };

  // 单词拼写：点击字母
  const handleLetterClick = (letter) => {
    // 如果已经完成拼写，不允许继续点击
    if (isSpellingComplete) return;
    
    // 如果字母已被使用，不允许再次点击
    if (letter.used) return;
    
    // 添加字母到已选择列表
    const newSelectedLetters = [...selectedLetters, letter];
    setSelectedLetters(newSelectedLetters);
    
    // 标记字母为已使用，而不是从数组中移除
    const newAvailableLetters = availableLetters.map(l => 
      l.id === letter.id ? { ...l, used: true } : l
    );
    setAvailableLetters(newAvailableLetters);
    
    // 如果选择完所有字母，自动检查拼写
    const currentQuestion = questions[currentQuestionIndex];
    const correctLettersCount = smartSplitWord(currentQuestion.correctWord).length;
    if (newSelectedLetters.length === correctLettersCount) {
      setTimeout(() => {
        checkSpelling(newSelectedLetters, currentQuestion);
      }, 300);
    }
  };

  // 单词拼写：移除已选择的字母（点击左边字母时）
  const handleRemoveLetter = (index) => {
    // 如果已经完成拼写，不允许移除
    if (isSpellingComplete) return;
    
    // 获取要移除的字母
    const removedLetter = selectedLetters[index];
    
    // 从已选择列表中移除
    const newSelectedLetters = selectedLetters.filter((_, i) => i !== index);
    setSelectedLetters(newSelectedLetters);
    
    // 将字母标记为未使用，使其在右边重新显示
    const newAvailableLetters = availableLetters.map(l => 
      l.id === removedLetter.id ? { ...l, used: false } : l
    );
    setAvailableLetters(newAvailableLetters);
    
    // 清除该位置及之后的拼写状态
    const newSpellingStatus = [...spellingStatus];
    for (let i = index; i < newSpellingStatus.length; i++) {
      newSpellingStatus[i] = '';
    }
    setSpellingStatus(newSpellingStatus);
  };

  // 单词拼写：检查拼写是否正确
  const checkSpelling = (selectedLetters, question) => {
    const userWord = selectedLetters.map(l => l.letter).join('');
    const correctWord = question.correctWord;
    
    // 使用智能分割来正确处理省略号
    const correctLetters = smartSplitWord(correctWord);
    
    // 检查每个字母是否正确
    const status = selectedLetters.map((letter, index) => {
      return correctLetters[index] === letter.letter ? 'correct' : 'incorrect';
    });
    
    setSpellingStatus(status);
    setIsSpellingComplete(true);
    
    // 判断是否全部正确
    const allCorrect = status.every(s => s === 'correct');
    
    if (allCorrect) {
      // 播放正确音效
      const correctAudio = new Audio('/audio/正确.MP3');
      correctAudio.play().catch(error => {
        console.error('播放正确音效失败:', error);
      });
    } else {
      // 播放错误音效
      const errorAudio = new Audio('/audio/错误.MP3');
      errorAudio.play().catch(error => {
        console.error('播放错误音效失败:', error);
      });
      // 不再自动重置，需要用户点击刷新按钮
    }
  };
  
  // 单词拼写：刷新题目（重新开始）
  const handleSpellingRefresh = () => {
    const currentQuestion = questions[currentQuestionIndex];
    if (currentQuestion) {
      initSpellingQuestion(currentQuestion);
    }
  };

  // 根据单词长度计算字体大小（词图匹配 - 选择区域的卡片）
  const getWordMatchFontSize = (word) => {
    const length = word.length;
    if (length <= 4) return '44px';
    if (length <= 6) return '40px';
    if (length <= 8) return '38px';
    if (length <= 10) return '34px';
    if (length <= 12) return '30px';
    if (length <= 16) return '28px';
    return '22px';
  };

  // 根据单词长度计算字体大小（听音选词 - 选项卡片）
  const getListenOptionFontSize = (word) => {
    const length = word.length;
    if (length <= 4) return '55px';
    if (length <= 6) return '50px';
    if (length <= 8) return '49px';
    if (length <= 10) return '46px';
    if (length <= 12) return '44px';
    if (length <= 16) return '41px';
    return '36px';
  };

  // 根据单词长度计算拼接卡片的字体大小
  const getCombiningCardFontSize = (word) => {
    const length = word.length;
    if (length <= 4) return '80px';
    if (length <= 6) return '72px';
    if (length <= 8) return '64px';
    if (length <= 10) return '56px';
    if (length <= 12) return '48px';
    if (length <= 15) return '40px';
    return '32px';
  };

  // 根据单词长度计算卡片袋中的字体大小
  const getCardDetailFontSize = (word) => {
    const length = word.length;
    if (length <= 4) return '56px';
    if (length <= 6) return '52px';
    if (length <= 8) return '48px';
    if (length <= 10) return '44px';
    if (length <= 12) return '40px';
    if (length <= 15) return '36px';
    return '32px';
  };

  // 上一题
  const previousQuestion = () => {
    // 立即完成当前正在匹配的卡片（如果有）
    if (questionType === QUESTION_TYPES.WORD_PICTURE_MATCH) {
      finishCurrentMatching();
    }
    
    if (currentQuestionIndex > 0) {
      // 如果是词图匹配，先保存当前题目的状态
      if (questionType === QUESTION_TYPES.WORD_PICTURE_MATCH) {
        saveCurrentMatchState(currentQuestionIndex);
      }
      
      setCurrentQuestionIndex(prev => prev - 1);
      
      if (questionType === QUESTION_TYPES.WORD_PICTURE_MATCH) {
        // 恢复上一题的答题状态
        setTimeout(() => {
          restoreMatchState(currentQuestionIndex - 1);
        }, 0);
      } else if (questionType === QUESTION_TYPES.LISTEN_SELECT) {
        resetListenQuestion();
        // 延迟播放音频，等待状态更新
        setTimeout(() => {
          playListenAudioAfterQuestionChange(currentQuestionIndex - 1);
        }, 100);
      } else if (questionType === QUESTION_TYPES.WORD_SPELLING) {
        resetSpellingQuestion();
        // 延迟初始化拼写题目
        setTimeout(() => {
          const question = questions[currentQuestionIndex - 1];
          initSpellingQuestion(question);
        }, 100);
      }
    } else if (currentQuestionIndex === 0 && questionType === QUESTION_TYPES.LISTEN_SELECT && wordPictureQuestions.length > 0) {
      // 如果是听音选词的第一题，切换回词图匹配的最后一题
      // 先停止当前播放的音频
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
      }
      setQuestionType(QUESTION_TYPES.WORD_PICTURE_MATCH);
      setCurrentQuestionIndex(wordPictureQuestions.length - 1);
      // 恢复词图匹配最后一题的状态
      setTimeout(() => {
        restoreMatchState(wordPictureQuestions.length - 1);
      }, 0);
    } else if (currentQuestionIndex === 0 && questionType === QUESTION_TYPES.WORD_SPELLING && listenSelectQuestions.length > 0) {
      // 如果是单词拼写的第一题，切换回听音选词的最后一题
      setQuestionType(QUESTION_TYPES.LISTEN_SELECT);
      setCurrentQuestionIndex(listenSelectQuestions.length - 1);
      resetListenQuestion();
      // 延迟播放音频，等待状态更新
      setTimeout(() => {
        if (listenSelectQuestions[listenSelectQuestions.length - 1]) {
          playListenAudioDirectly(listenSelectQuestions[listenSelectQuestions.length - 1]);
        }
      }, 100);
    }
  };

  // 下一题
  const nextQuestion = () => {
    // 立即完成当前正在匹配的卡片（如果有）
    if (questionType === QUESTION_TYPES.WORD_PICTURE_MATCH) {
      finishCurrentMatching();
    }
    
    if (currentQuestionIndex < questions.length - 1) {
      // 如果是词图匹配，先保存当前题目的状态
      if (questionType === QUESTION_TYPES.WORD_PICTURE_MATCH) {
        saveCurrentMatchState(currentQuestionIndex);
      }
      
      setCurrentQuestionIndex(prev => prev + 1);
      
      if (questionType === QUESTION_TYPES.WORD_PICTURE_MATCH) {
        // 恢复下一题的答题状态
        setTimeout(() => {
          restoreMatchState(currentQuestionIndex + 1);
        }, 0);
      } else if (questionType === QUESTION_TYPES.LISTEN_SELECT) {
        resetListenQuestion();
        // 延迟播放音频，等待状态更新
        setTimeout(() => {
          playListenAudioAfterQuestionChange(currentQuestionIndex + 1);
        }, 100);
      } else if (questionType === QUESTION_TYPES.WORD_SPELLING) {
        resetSpellingQuestion();
        // 延迟初始化拼写题目
        setTimeout(() => {
          const question = questions[currentQuestionIndex + 1];
          initSpellingQuestion(question);
        }, 100);
      }
    } else if (currentQuestionIndex === questions.length - 1 && questionType === QUESTION_TYPES.WORD_PICTURE_MATCH && listenSelectQuestions.length > 0) {
      // 如果是词图匹配的最后一题，立即完成当前匹配（如果有），然后切换到听音选词
      finishCurrentMatching();
      saveCurrentMatchState(currentQuestionIndex);
      setQuestionType(QUESTION_TYPES.LISTEN_SELECT);
      setCurrentQuestionIndex(0);
      resetListenQuestion();
      // 播放听音选词第一题的音频
      setTimeout(() => {
        if (listenSelectQuestions[0]) {
          playListenAudioDirectly(listenSelectQuestions[0]);
        }
      }, 100);
    } else if (currentQuestionIndex === questions.length - 1 && questionType === QUESTION_TYPES.LISTEN_SELECT && wordSpellingQuestions.length > 0) {
      // 如果是听音选词的最后一题，切换到单词拼写的第一题
      setQuestionType(QUESTION_TYPES.WORD_SPELLING);
      setCurrentQuestionIndex(0);
      resetSpellingQuestion();
      // 初始化单词拼写第一题
      setTimeout(() => {
        if (wordSpellingQuestions[0]) {
          initSpellingQuestion(wordSpellingQuestions[0]);
        }
      }, 100);
    }
  };

  // 渲染课时选择
  const renderLessonSelection = () => {
    const lessons = wordsData?.lessons || [];
    
    return (
      <div className="lesson-selection">
        <div className="lesson-selection-title">
          请选择课时(多选):
        </div>
        <Select
          mode="multiple"
          className="lesson-select"
          placeholder="请选择课时"
          value={selectedLessons}
          onChange={setSelectedLessons}
          style={{ width: '100%' }}
          maxTagCount={1}
          maxTagPlaceholder={() => ''}
          options={lessons.map(lesson => ({
            label: lesson.lesson_title,
            value: lesson.lesson_id
          }))}
        />
      </div>
    );
  };

  // 渲染题型选择
  const renderQuestionTypeSelection = () => (
    <div className="question-type-selection">
      <div className="question-type-title">
        题目类型:
      </div>
      <div className="question-type-buttons">
        <button
          className={`question-type-btn ${questionType === QUESTION_TYPES.WORD_PICTURE_MATCH ? 'active' : ''}`}
          onClick={() => {
            if (questionType !== QUESTION_TYPES.WORD_PICTURE_MATCH) {
              // 清除听音选词的自动跳转定时器（如果有）
              if (listenAutoNextTimerRef.current) {
                clearTimeout(listenAutoNextTimerRef.current);
                listenAutoNextTimerRef.current = null;
              }
              setQuestionType(QUESTION_TYPES.WORD_PICTURE_MATCH);
              setCurrentQuestionIndex(0);
              // 恢复第一题的答题状态
              setTimeout(() => {
                restoreMatchState(0);
              }, 0);
            }
          }}
        >
          词图匹配
        </button>
        <button
          className={`question-type-btn ${questionType === QUESTION_TYPES.LISTEN_SELECT ? 'active' : ''}`}
          onClick={() => {
            if (questionType !== QUESTION_TYPES.LISTEN_SELECT) {
              // 清除听音选词的自动跳转定时器（如果有）
              if (listenAutoNextTimerRef.current) {
                clearTimeout(listenAutoNextTimerRef.current);
                listenAutoNextTimerRef.current = null;
              }
              setQuestionType(QUESTION_TYPES.LISTEN_SELECT);
              setCurrentQuestionIndex(0);
              resetListenQuestion();
            }
          }}
        >
          听音选词
        </button>
        <button
          className={`question-type-btn ${questionType === QUESTION_TYPES.WORD_SPELLING ? 'active' : ''}`}
          onClick={() => {
            if (questionType !== QUESTION_TYPES.WORD_SPELLING) {
              // 清除听音选词的自动跳转定时器
              if (listenAutoNextTimerRef.current) {
                clearTimeout(listenAutoNextTimerRef.current);
                listenAutoNextTimerRef.current = null;
              }
              setQuestionType(QUESTION_TYPES.WORD_SPELLING);
              setCurrentQuestionIndex(0);
              resetSpellingQuestion();
              // 延迟初始化第一题
              if (wordSpellingQuestions.length > 0) {
                setTimeout(() => {
                  initSpellingQuestion(wordSpellingQuestions[0]);
                }, 100);
              }
            }
          }}
        >
          单词拼写
        </button>
      </div>
    </div>
  );

  // 渲染词图匹配练习
  const renderWordPictureMatch = () => {
    if (questions.length === 0 || currentQuestionIndex >= questions.length) {
      return null;
    }

    const currentQuestion = questions[currentQuestionIndex];
    
    // 检查当前题目和图片、单词数据是否存在
    if (!currentQuestion || !currentQuestion.pictures || !currentQuestion.words) {
      return null;
    }

    return (
      <div className="word-picture-match">
        <div className="question-progress">
          第 {currentQuestionIndex + 1} 题 / 共 {questions.length} 题
        </div>

        {/* 图片区域 */}
        <div className="pictures-area">
          <div className="pictures-grid">
            {currentQuestion.pictures.map(picture => {
              const isMatched = matchedPairs.find(p => p.pictureId === picture.id);
              const isCombining = combiningItems.includes(picture.id);
              const isSelected = selectedPicture?.id === picture.id;
              const isError = errorItems.picture === picture.id;
              
              // 如果已匹配且不在动画中，保留占位空间但完全隐藏
              if (isMatched && !isCombining) {
                return (
                  <div
                    key={picture.id}
                    className="picture-item"
                    style={{ 
                      opacity: 0, 
                      visibility: 'hidden',
                      pointerEvents: 'none'
                    }}
                  >
                    <img src={picture.image_url} alt={picture.chinese} style={{ opacity: 0 }} />
                  </div>
                );
              }
              
              // 如果正在飞向中间
              if (isCombining) {
                return (
                  <div
                    key={picture.id}
                    className="picture-item flying-to-center"
                  >
                    <img src={picture.image_url} alt={picture.chinese} />
                  </div>
                );
              }
              
              return (
                <div
                  key={picture.id}
                  className={`picture-item ${isSelected ? 'selected' : ''} ${isError ? 'error error-ripple' : ''}`}
                  onClick={() => handlePictureClick(picture)}
                >
                  <img src={picture.image_url} alt={picture.chinese} />
                </div>
              );
            })}
          </div>
        </div>

        {/* 单词区域 */}
        <div className="words-area">
          <div className="exercises-words-grid">
            {currentQuestion.words.map(word => {
              const isMatched = matchedPairs.find(p => p.wordId === word.id);
              const isCombining = combiningItems.includes(word.id);
              const isSelected = selectedWord?.id === word.id;
              const isError = errorItems.word === word.id;
              
              // 如果已匹配且不在动画中，保留占位空间但完全隐藏
              if (isMatched && !isCombining) {
                return (
                  <div
                    key={word.id}
                    className="word-item"
                    style={{ 
                      opacity: 0, 
                      visibility: 'hidden',
                      pointerEvents: 'none',
                      fontSize: getWordMatchFontSize(word.word)
                    }}
                  >
                    {word.word}
                  </div>
                );
              }
              
              // 如果正在飞向中间
              if (isCombining) {
                return (
                  <div
                    key={word.id}
                    className="word-item flying-to-center"
                    style={{ fontSize: getWordMatchFontSize(word.word) }}
                  >
                    {word.word}
                  </div>
                );
              }
              
              return (
                <div
                  key={word.id}
                  className={`word-item ${isSelected ? 'selected' : ''} ${isError ? 'error error-ripple' : ''}`}
                  onClick={() => handleWordClick(word)}
                  style={{ fontSize: getWordMatchFontSize(word.word) }}
                >
                  {word.word}
                </div>
              );
            })}
          </div>
        </div>

        {/* 组合卡片（正在组合和播放音频时显示） */}
        {combiningPair && (
          <div className={`combining-card-container ${combiningPair.isFalling ? 'combining-fall-animation' : 'combining-show-animation'}`}>
            <div className="combining-card">
              <div className="combining-card-image">
                <img src={combiningPair.imageUrl} alt={combiningPair.chinese} />
              </div>
              <div className="combining-card-word" style={{ fontSize: getCombiningCardFontSize(combiningPair.word) }}>
                {combiningPair.word}
              </div>
            </div>
          </div>
        )}

        {/* 卡片袋区域 */}
        {showCardBag && (
          <div className="card-bag">
            <img 
              src={cardsImage} 
              alt="卡片袋" 
              className="card-bag-image" 
              onClick={() => setShowCardsDetail(!showCardsDetail)}
              style={{ cursor: 'pointer' }}
            />
          </div>
        )}

        {/* 卡片详情弹窗 */}
        {showCardsDetail && (
          <div className="cards-detail-overlay" onClick={() => setShowCardsDetail(false)}>
            <div className="cards-detail-list" onClick={(e) => e.stopPropagation()}>
              {matchedPairs.map((pair, index) => (
                <div 
                  key={index} 
                  className="matched-card-detail"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="matched-card-detail-image">
                    <img src={pair.imageUrl} alt={pair.chinese} />
                  </div>
                  <div className="matched-card-detail-word" style={{ fontSize: getCardDetailFontSize(pair.word) }}>
                    {pair.word}
                  </div>
                  <button 
                    className={`matched-card-detail-play ${playingCardIndex === index ? 'playing' : ''}`}
                    onClick={() => handlePlayAudio(pair, index)}
                    title="播放发音"
                    disabled={playingCardIndex === index}
                  >
                    {playingCardIndex === index ? (
                      <div className="sound-wave">
                        <div className="wave-bar"></div>
                        <div className="wave-bar"></div>
                        <div className="wave-bar"></div>
                        <div className="wave-bar"></div>
                        <div className="wave-bar"></div>
                        <div className="wave-bar"></div>
                      </div>
                    ) : (
                      <img src={playIcon} alt="播放" className="play-icon" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // 渲染听音选词练习
  const renderListenSelect = () => {
    if (questions.length === 0 || currentQuestionIndex >= questions.length) {
      return null;
    }

    const currentQuestion = questions[currentQuestionIndex];
    
    // 检查当前题目和选项是否存在
    if (!currentQuestion || !currentQuestion.options) {
      return null;
    }

    return (
      <div className="listen-select">
        <div className="question-progress">
          第 {currentQuestionIndex + 1} 题 / 共 {questions.length} 题
          <span className="question-instruction">听音选词 —— 选择你所听到的单词或词组</span>
        </div>

        {/* 听音按钮 */}
        <div className="listen-button-container">
          <button 
            className={`listen-button ${isPlayingAudio ? 'playing' : ''}`}
            onClick={playListenAudio}
            disabled={isPlayingAudio}
          >
            <img src={listenIcon} alt="听" />
          </button>
        </div>

        {/* 选项区域 */}
        <div className="listen-options">
          {currentQuestion.options.map(option => {
            const isSelected = selectedOption?.id === option.id;
            const isError = errorOption === option.id;
            const showCorrect = isCorrect && isSelected;
            
            return (
              <div
                key={option.id}
                className={`listen-option ${isSelected ? (showCorrect ? 'correct' : '') : ''} ${isError ? 'error error-ripple' : ''}`}
                onClick={() => handleListenOptionClick(option)}
              >
                <div className="listen-option-word" style={{ fontSize: getListenOptionFontSize(option.word) }}>
                  {option.word}
                </div>
                {showCorrect && (
                  <div className="listen-option-check">✓</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // 渲染单词拼写练习
  const renderWordSpelling = () => {
    if (questions.length === 0 || currentQuestionIndex >= questions.length) {
      return null;
    }

    const currentQuestion = questions[currentQuestionIndex];
    
    // 检查当前题目是否存在
    if (!currentQuestion) {
      return null;
    }

    return (
      <div className="word-spelling">
        <div className="question-progress">
          第 {currentQuestionIndex + 1} 题 / 共 {questions.length} 题
          <span className="question-instruction">单词拼写 —— 点击字母拼出正确的单词、根据中文意思、把打散的字母组成正确的单词或词组。</span>
        </div>

        <div className="spelling-container">
          {/* 左侧区域 */}
          <div className="spelling-left">
            {/* 中文翻译 */}
            <div className="spelling-chinese">{currentQuestion.chinese}</div>
            
            {/* 字母显示框 */}
            <div className="spelling-display">
              {selectedLetters.map((letter, index) => {
                const charType = getCharacterType(letter.letter);
                const displayChar = getCharacterDisplay(letter.letter);
                return (
                  <div 
                    key={letter.id}
                    className={`spelling-letter ${spellingStatus[index] ? `spelling-letter-${spellingStatus[index]}` : ''} spelling-letter-${charType}`}
                    onClick={() => handleRemoveLetter(index)}
                    style={{ cursor: 'pointer' }}
                  >
                    {displayChar}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 右侧字母区域 */}
          <div className="spelling-right">
            <div className="spelling-letters-container">
              {availableLetters.map(letter => {
                const charType = getCharacterType(letter.letter);
                const displayChar = getCharacterDisplay(letter.letter);
                return (
                  <div
                    key={letter.id}
                    className={`spelling-letter-option spelling-letter-${charType} ${letter.used ? 'spelling-letter-used' : ''}`}
                    onClick={() => handleLetterClick(letter)}
                  >
                    {displayChar}
                  </div>
                );
              })}
            </div>
            
            {/* 刷新按钮 */}
            <div 
              className="spelling-refresh-btn"
              onClick={handleSpellingRefresh}
              title="重新开始"
            >
              <img src={refreshIcon} alt="刷新" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 渲染主要内容
  const renderMainContent = () => {
    // 如果没有选择课时或题型，显示提示
    if (selectedLessons.length === 0 || !questionType) {
      return (
        <div className="exercises-empty">
          <div className="exercises-empty-icon">📝</div>
          <div className="exercises-empty-text">
            请选择课时和题型开始练习
          </div>
        </div>
      );
    }

    // 如果正在加载
    if (loading) {
      return (
        <div className="exercises-empty">
          <div className="exercises-empty-text">加载中...</div>
        </div>
      );
    }

    // 如果没有题目数据
    if (!exerciseStarted || questions.length === 0) {
      return (
        <div className="exercises-empty">
          <div className="exercises-empty-text">暂无可用的练习题目</div>
        </div>
      );
    }

    if (questionType === QUESTION_TYPES.WORD_PICTURE_MATCH) {
      return renderWordPictureMatch();
    }

    if (questionType === QUESTION_TYPES.LISTEN_SELECT) {
      return renderListenSelect();
    }

    if (questionType === QUESTION_TYPES.WORD_SPELLING) {
      return renderWordSpelling();
    }

    return (
      <div className="exercises-empty">
        <div className="exercises-empty-text">该题型正在开发中...</div>
      </div>
    );
  };

  return (
    <div className="exercises-container">
      {/* 主要区域 */}
      <div className="exercises-main">
        {/* 顶部选择区域 */}
        <div className="exercises-selection">
          {renderLessonSelection()}
          {renderQuestionTypeSelection()}
        </div>

        {/* 练习内容区域 */}
        <div className="exercises-content">
          {renderMainContent()}
        </div>
      </div>

      {/* 底部区域 */}
      <div className="exercises-bottom">
        <div className="exercises-navigation">
          <button 
            className="nav-btn prev-btn"
            onClick={previousQuestion}
            disabled={
              !exerciseStarted || 
              questions.length === 0 || 
              selectedLessons.length === 0 || 
              (currentQuestionIndex === 0 && questionType === QUESTION_TYPES.WORD_PICTURE_MATCH)
            }
          >
            上一题
          </button>
          {exerciseStarted && questions.length > 0 && selectedLessons.length > 0 && (
            <div className="nav-info">
              第 {currentQuestionIndex + 1} / {questions.length} 题
            </div>
          )}
          <button 
            className="nav-btn next-btn"
            onClick={nextQuestion}
            disabled={
              !exerciseStarted || 
              questions.length === 0 || 
              selectedLessons.length === 0 || 
              (currentQuestionIndex === questions.length - 1 && questionType === QUESTION_TYPES.WORD_SPELLING) ||
              (currentQuestionIndex === questions.length - 1 && questionType === QUESTION_TYPES.LISTEN_SELECT && wordSpellingQuestions.length === 0)
            }
          >
            下一题
          </button>
        </div>
      </div>
    </div>
  );
};

export default Exercises;

